"use client";

import { useState, useMemo, useRef } from "react";
import axios from "axios";
import useSWR from "swr";
import { useBlogHomepage } from "@/hooks/useBlogHomepage";
import {
  ArticleCard,
  ArticleCardSkeleton,
  type ArticleCardData,
} from "@/components/articles/ArticleCard";
import { NewsletterInline } from "@/components/articles/NewsletterInline";
import {
  LuBookOpen,
  LuSearch,
  LuSparkles,
  LuMapPin,
  LuX,
  LuCompass,
  LuGlobe,
  LuWallet,
  LuGraduationCap,
  LuFileText,
  LuClipboardList,
  LuBuilding2,
  LuHouse,
  LuBriefcase,
  LuLandmark,
  LuNewspaper,
  LuLayoutGrid,
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
} from "react-icons/lu";

const STUDY_ABROAD_COUNTRIES = [
  "Australia",
  "Austria",
  "Belgium",
  "Canada",
  "China",
  "Czech Republic",
  "Denmark",
  "Finland",
  "France",
  "Germany",
  "Hungary",
  "India",
  "Ireland",
  "Italy",
  "Japan",
  "Malaysia",
  "Netherlands",
  "New Zealand",
  "Norway",
  "Poland",
  "Portugal",
  "Singapore",
  "South Korea",
  "Spain",
  "Sweden",
  "Switzerland",
  "Thailand",
  "Turkey",
  "UAE",
  "United Kingdom",
  "United States",
];

const API = process.env.NEXT_PUBLIC_API_BASE_URL;
const fetcher = (url: string) => axios.get(url).then((r) => r.data);

const CATEGORY_ICON: Record<string, React.ElementType> = {
  "start-here": LuCompass,
  countries: LuGlobe,
  "costs-and-budgeting": LuWallet,
  "scholarships-and-funding": LuGraduationCap,
  "visa-and-documents": LuFileText,
  "tests-and-preparation": LuClipboardList,
  "find-and-apply": LuBuilding2,
  "find-and-apply-to-universities": LuBuilding2,
  "student-life-abroad": LuHouse,
  "work-and-career": LuBriefcase,
  "pr-and-settlement": LuLandmark,
  "student-stories": LuBookOpen,
  "news-and-updates": LuNewspaper,
};

function CategoryTabs({
  categories,
  activeCategory,
  homepageLoading,
  onCategoryClick,
  onClearAll,
}: {
  categories: { key: string; displayName: string; count: number }[];
  activeCategory: string | null;
  homepageLoading: boolean;
  onCategoryClick: (key: string) => void;
  onClearAll: () => void;
}) {
  const filteredCategories = categories.filter((c) => c.key !== "START_HERE");
  const allActive = activeCategory === null;

  if (homepageLoading) {
    return (
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-9 w-28 shrink-0 rounded-full bg-gray-100 dark:bg-white/10 animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
      <button
        type="button"
        onClick={onClearAll}
        className={`inline-flex items-center gap-1.5 shrink-0 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
          allActive
            ? "bg-indigo-600 text-white shadow-sm"
            : "bg-gray-100 dark:bg-white/8 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15"
        }`}
      >
        <LuLayoutGrid
          className={`w-3.5 h-3.5 ${allActive ? "text-white" : "text-gray-400 dark:text-gray-500"}`}
        />
        All
      </button>

      {filteredCategories.map((cat) => {
        const Icon = CATEGORY_ICON[cat.key] ?? LuBookOpen;
        const isActive = activeCategory === cat.key;
        return (
          <button
            key={cat.key}
            type="button"
            onClick={() => onCategoryClick(cat.key)}
            className={`inline-flex items-center gap-1.5 shrink-0 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              isActive
                ? "bg-indigo-600 text-white shadow-sm"
                : "bg-gray-100 dark:bg-white/8 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15"
            }`}
          >
            <Icon
              className={`w-3.5 h-3.5 ${isActive ? "text-white" : "text-gray-400 dark:text-gray-500"}`}
            />
            {cat.displayName}
            {cat.count > 0 && (
              <span
                className={`text-xs ${isActive ? "text-indigo-200" : "text-gray-400 dark:text-gray-500"}`}
              >
                {cat.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

const ARTICLES_PAGE_SIZE = 12;

function getPageNumbers(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);

  const result: (number | "…")[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - (sorted[i - 1] as number) > 1) result.push("…");
    result.push(p);
  });
  return result;
}

export default function ArticlesPage() {
  const { data: homepage, loading: homepageLoading } = useBlogHomepage();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeCountry, setActiveCountry] = useState<string>("");
  const [page, setPage] = useState(1);
  const resultsRef = useRef<HTMLElement>(null);

  const allBlogsUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(ARTICLES_PAGE_SIZE));
    if (activeCategory) params.set("category", activeCategory);
    if (search.trim()) params.set("search", search.trim());
    if (activeCountry) params.set("country", activeCountry);
    return `${API}/articles?${params.toString()}`;
  }, [activeCategory, search, activeCountry, page]);

  const { data: allBlogsData, isLoading: allBlogsLoading } = useSWR(
    allBlogsUrl,
    fetcher,
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  const total = allBlogsData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / ARTICLES_PAGE_SIZE));

  const activeCategorySlug = useMemo(
    () =>
      homepage?.categories?.find((c) => c.key === activeCategory)?.urlSlug ??
      null,
    [homepage, activeCategory],
  );

  const { data: categoryData } = useSWR(
    activeCategory && activeCategorySlug
      ? `${API}/articles/category/${activeCategorySlug}?page=1&limit=1`
      : null,
    fetcher,
    { revalidateOnFocus: false },
  );

  const featuredArticle = activeCategory
    ? (categoryData?.featuredArticle ?? null)
    : null;

  const allArticles: ArticleCardData[] = allBlogsData?.articles ?? [];

  function handleCategoryClick(key: string) {
    setActiveCategory(activeCategory === key ? null : key);
    setSearch("");
    setPage(1);
  }

  function handleClearAll() {
    setActiveCategory(null);
    setActiveCountry("");
    setSearch("");
    setPage(1);
  }

  function handleCountryChange(country: string) {
    setActiveCountry(country);
    setPage(1);
  }

  function handleSearch(val: string) {
    setSearch(val);
    setPage(1);
  }

  function handlePageChange(next: number) {
    setPage(next);
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const displayedArticles = allArticles;
  const categories = homepage?.categories ?? [];

  return (
    <div className="min-h-screen">
      {/* ── Hero + Search ── */}
      <section className="relative overflow-hidden bg-linear-to-br from-indigo-600 via-indigo-700 to-purple-800 dark:from-indigo-900 dark:via-indigo-950 dark:to-purple-950">
        <div className="relative max-w-7xl mx-auto px-5 py-14 lg:px-10for lg:py-20">
          <div className="flex items-center gap-2 mb-4">
            <LuMapPin className="w-4 h-4 text-indigo-300" />
            <span className="text-indigo-200 text-sm font-medium">
              For Bangladeshi students
            </span>
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4 leading-tight max-w-3xl">
            Your complete guide to
            <br />
            <span className="text-indigo-200">studying abroad</span>
          </h1>
          <p className="text-base lg:text-lg text-indigo-100 max-w-2xl leading-relaxed mb-8">
            Expert guides, real stories, and practical advice, everything you
            need for every step of your study abroad journey.
          </p>

          <div className="relative max-w-xl">
            <input
              type="text"
              placeholder="Search guides, countries, scholarships…"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-12 pr-10 py-3 rounded-xl bg-white/15 border border-white/25 text-white placeholder:text-indigo-300 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
            />
            <LuSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-300 pointer-events-none" />
            {search && (
              <button
                onClick={() => handleSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-indigo-300 hover:text-white transition-colors"
              >
                <LuX className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="py-10 lg:py-14">
        <div className="w-[92%] max-w-6xl mx-auto space-y-14">
          {/* ── Start Here (standalone — always visible, independent of tab) ── */}
          {!search && (
            <section>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <LuBookOpen className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    Start Here
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    New to studying abroad? Start with these.
                  </p>
                </div>
              </div>
              {homepageLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[1, 2].map((i) => (
                    <ArticleCardSkeleton key={i} featured />
                  ))}
                </div>
              ) : (homepage?.startHereArticles?.length ?? 0) > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {homepage!.startHereArticles.slice(0, 4).map((a) => (
                    <ArticleCard key={a.id} article={a} featured />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500 py-6 text-center">
                  No Start Here posts yet.
                </p>
              )}
            </section>
          )}

          {/* ── Popular Guides (standalone — always visible, independent of tab) ── */}
          {!search && (homepage?.popularGuides?.length ?? 0) > 0 && (
            <section>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <LuSparkles className="w-5 h-5 text-amber-500" />
                    Popular Guides
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    The most-read guides from our community.
                  </p>
                </div>
              </div>
              {homepageLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <ArticleCardSkeleton key={i} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {homepage!.popularGuides.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ── Browse by Category (tabs) ── */}
          <div>
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Browse by Category
            </h2>
            <CategoryTabs
              categories={categories}
              activeCategory={activeCategory}
              homepageLoading={homepageLoading}
              onCategoryClick={handleCategoryClick}
              onClearAll={handleClearAll}
            />
          </div>

          {/* ── Tab content: All Blogs / selected category ── */}
          <section ref={resultsRef} className="scroll-mt-28">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  {activeCategory &&
                    (() => {
                      const Icon = CATEGORY_ICON[activeCategory] ?? LuBookOpen;
                      return (
                        <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      );
                    })()}
                  {activeCategory
                    ? (categories.find((c) => c.key === activeCategory)
                        ?.displayName ?? activeCategory)
                    : search
                      ? `Results for "${search}"`
                      : activeCountry
                        ? `Posts from ${activeCountry}`
                        : "All Blog Posts"}
                </h2>
              </div>

              <div className="flex items-center gap-2">
                <div className="relative">
                  <select
                    value={activeCountry}
                    onChange={(e) => handleCountryChange(e.target.value)}
                    className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:border-indigo-400 cursor-pointer"
                  >
                    <option value="">All countries</option>
                    {STUDY_ABROAD_COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <LuChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                </div>
                {(activeCategory || search || activeCountry) && (
                  <button
                    onClick={handleClearAll}
                    className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    <LuX className="w-4 h-4" /> Clear
                  </button>
                )}
              </div>
            </div>

            {/* Featured article for active category */}
            {featuredArticle && (
              <div className="mb-6">
                <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-3">
                  Featured
                </p>
                <ArticleCard article={featuredArticle} featured />
              </div>
            )}

            {allBlogsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                  <ArticleCardSkeleton key={i} />
                ))}
              </div>
            ) : displayedArticles.length === 0 ? (
              <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                <LuBookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-base font-medium">No blog posts found</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {displayedArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page <= 1}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/8 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label="Previous page"
                    >
                      <LuChevronLeft className="w-4 h-4" />
                    </button>

                    {getPageNumbers(page, totalPages).map((p, i) =>
                      p === "…" ? (
                        <span
                          key={`ellipsis-${i}`}
                          className="w-9 h-9 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          onClick={() => handlePageChange(p)}
                          className={`inline-flex items-center justify-center w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                            p === page
                              ? "bg-indigo-600 text-white shadow-sm"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/8"
                          }`}
                        >
                          {p}
                        </button>
                      ),
                    )}

                    <button
                      type="button"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= totalPages}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/8 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label="Next page"
                    >
                      <LuChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </div>

        {/* ── Newsletter ── */}
        <div className="w-[46%] mx-auto mt-16">
          <NewsletterInline />
        </div>
      </div>
    </div>
  );
}
